// src/hooks/useInactivityWatchdog.ts
import { useEffect, useRef, useCallback } from "react";
import { N8N_NUDGE_EVENT_URL } from "../config/n8n";
import {
  ENABLE_WATCHDOG,
  WATCHDOG_INTERVAL_MS,
  WATCHDOG_IDLE_THRESHOLD_MS,
  WATCHDOG_MAX_NUDGES,
} from "../config/watchdog";

export interface NudgeReplyPayload {
  text: string;
  audioBase64?: string;
  mimeType?: string;
  audioUrl?: string;
  confetti?: boolean;
}

interface WatchdogOptions {
  sessionId: string;
  accessCode?: string | null;
  /** Optional per-instance override of idle threshold (otherwise uses env-config). */
  idleThresholdMs?: number;
  /** Return true to suppress nudges (e.g., timer visible, typing, loading, init overlay). */
  suppressIf?: () => boolean;
  /** Small local echo like "💤 User has been inactive for 1 minute(s)." */
  onLocalNudge?: (msg: string) => void;
  /** Full reply from nudge flow → append assistant bubble / play TTS in ChatContainer */
  onNudgeReply?: (payload: NudgeReplyPayload) => void;
}

export function useInactivityWatchdog({
  sessionId,
  accessCode,
  idleThresholdMs,
  suppressIf,
  onLocalNudge,
  onNudgeReply,
}: WatchdogOptions) {
  const lastActiveRef = useRef<number>(Date.now());
  const pingInFlightRef = useRef(false);
  const nudgeCountRef = useRef(0); // how many nudges sent this idle cycle
  const asleepRef = useRef(false); // goes true after hitting max nudges

  const resetActivity = useCallback(() => {
    if (!ENABLE_WATCHDOG) return;
    lastActiveRef.current = Date.now();
    nudgeCountRef.current = 0;  // ✅ reset when user moves or types
    asleepRef.current = false;  // ✅ wake watchdog
  }, []);

  const triggerNudge = useCallback(
    async (idleMs: number) => {
      if (!ENABLE_WATCHDOG) return;
      if (pingInFlightRef.current) return;
      if (suppressIf?.()) return;

      if (asleepRef.current) return; // ✅ watchdog sleeping, stop

      if (nudgeCountRef.current >= WATCHDOG_MAX_NUDGES) {
        asleepRef.current = true;
        if (process.env.NODE_ENV !== "production") {
          console.log(`[Watchdog] Max nudges (${WATCHDOG_MAX_NUDGES}) reached → sleeping`);
        }
        return;
      }

      nudgeCountRef.current++;
      if (process.env.NODE_ENV !== "production") {
        console.log(`[Watchdog] Nudge #${nudgeCountRef.current}/${WATCHDOG_MAX_NUDGES}`);
      }

      pingInFlightRef.current = true;

      const payload = {
        origin: "watchdog" as const,
        type: "nudgePing" as const,
        sessionId,
        accessCode: accessCode || "",
        idleDuration: idleMs,
      };

      try {
        const idleMin = Math.round(idleMs / 60000);
        onLocalNudge?.(`💤 User has been inactive for ${idleMin} minute(s).`);

        const res = await fetch(N8N_NUDGE_EVENT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const raw = await res.text();
        let data: any = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          data = {};
        }

        // ✅ normalize [ {...} ] → {...}
        if (Array.isArray(data) && data.length === 1) data = data[0];

        const msg: string =
          data?.message ?? data?.text ?? data?.reply ?? "";

        if (msg && onNudgeReply) {
          onNudgeReply({
            text: msg,
            audioBase64: data?.audioBase64,
            mimeType: data?.mimeType,
            audioUrl: data?.audioUrl,
            confetti: Boolean(data?.confetti),
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Watchdog] Nudge call failed:", err);
      } finally {
        pingInFlightRef.current = false;
        lastActiveRef.current = Date.now(); // reset idle window after a nudge
      }
    },
    [sessionId, accessCode, suppressIf, onLocalNudge, onNudgeReply]
  );

  useEffect(() => {
    if (!ENABLE_WATCHDOG) {
      if (process.env.NODE_ENV !== "production") {
        console.log("[Watchdog] Disabled");
      }
      return;
    }

    const effectiveThreshold = idleThresholdMs ?? WATCHDOG_IDLE_THRESHOLD_MS;

    const checkIdle = () => {
      if (suppressIf?.()) return;
      const now = Date.now();
      const idleMs = now - lastActiveRef.current;
      if (idleMs >= effectiveThreshold) {
        triggerNudge(idleMs);
      }
    };

    if (process.env.NODE_ENV !== "production") {
      console.log(
        `[Watchdog] Started interval=${WATCHDOG_INTERVAL_MS}ms threshold=${effectiveThreshold}ms`
      );
    }

    const intervalId = setInterval(checkIdle, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [idleThresholdMs, suppressIf, triggerNudge]);

  useEffect(() => {
    if (!ENABLE_WATCHDOG) return;
    const events: Array<keyof WindowEventMap> = [
      "click",
      "keydown",
      "mousemove",
      "touchstart",
      "scroll",
    ];
    events.forEach((ev) => window.addEventListener(ev, resetActivity));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetActivity));
    };
  }, [resetActivity]);

  return { resetActivity };
}
